/* eslint-disable */
import type { Expiration } from "../cash/expiration.pb";
import type { DigitalWalletToken } from "./digital_wallet.pb";
import type { AccountType } from "./transaction.pb";

export enum IssuedCardBrand {
  VISA = "VISA",
  MASTERCARD = "MASTERCARD",
}

export enum CardType {
  VIRTUAL = "VIRTUAL",
  PHYSICAL = "PHYSICAL",
}

export interface IssuedCard {
  fidelius_token?: string;
  expiration?: Expiration;
  security_code?: string;
  virtual?: boolean;
  /** A unique token used to identify this card on our system */
  token?: string;
  /** Whether or not the card is spendable */
  enabled?: boolean;
  /**
   * Whether or not funds are scheduled to move to the sponsoring bank and
   * the customer has opted in to the program
   */
  activated?: boolean;
  /**
   * Whether or not KYC has been performed at bank.
   * Deprecated as Square communicates KYC to issuers and this information can be obtained from
   * the customer.
   *
   * @deprecated
   */
  identity_verified?: boolean;
  /** Token used to identify card's customization (signature, color, etc.) */
  customization_token?: string;
  /**
   * Customers can have more than one issued card (virtual, physical, etc.)
   * so this flag will represent whether the card is the primary card or not
   */
  primary?: boolean;
  /** True if the card has been verified for activation. */
  activation_verified?: boolean;
  /** Timestamp when card was marked as inactive due to being lost, stolen, etc. */
  deactivated_at?: number;
  /** Card state */
  state?: IssuedCardCardState;
  /** Digital wallet tokens */
  digital_wallet_tokens?: DigitalWalletToken[];
  /**
   * Account type associated with card (PREPAID is Marqeta, DDA is Shazam)
   * Use card_issuer instead.
   *
   * @deprecated
   */
  account_type?: AccountType;
  /** Timestamp when card was ordered (for physical cards) */
  ordered_at?: number;
  /** Stored Value Token used to create card. This is also the user token for Marqeta */
  stored_value_token?: string;
  card_events?: CardEvent[];
  card_issuer?: IssuedCardCardIssuer;
  emergency_issue?: boolean;
  marqeta_issuer_data?: IssuedCardMarqetaIssuerData | undefined;
  shipping_method?: string;
  tracking_number?: string;
  /**
   * This field indicates whether or not the PIN has been set for a card. Warning, this might
   * not be populated for all responses and is currently designed for the Square Card product
   */
  pin_is_set?: boolean;
  customer_token?: string;
  /** Last four digits of the PAN */
  last_four?: string;
  /** Timestamp when card was activated. */
  activated_at?: number;
}

export enum IssuedCardCardState {
  ACTIVE = "ACTIVE",
  DISABLED = "DISABLED",
  TERMINATED = "TERMINATED",
  SUSPENDED = "SUSPENDED",
  VERIFIED = "VERIFIED",
  ISSUED = "ISSUED",
  UNDELIVERABLE = "UNDELIVERABLE",
  /** MONITORED - Card is essentially ACTIVE but transactions are given greater scrutiny. */
  MONITORED = "MONITORED",
  /** SHIPPED - The following are more detailed physical card states before activation. */
  SHIPPED = "SHIPPED",
  CANCELLED = "CANCELLED",
  REJECTED = "REJECTED",
  DESTROYED = "DESTROYED",
}

export enum IssuedCardCardIssuer {
  SHAZAM = "SHAZAM",
  MARQETA = "MARQETA",
  VISA_DPS = "VISA_DPS",
}

export interface IssuedCardMarqetaIssuerData {
  /**
   * The name of the Marqeta shard this card belongs on.
   * e.g. sqr, sqr2 ... sqr9
   */
  shard?: string;
}

export interface CardEvent {
  issued_card_token?: string;
  stored_value_token?: string;
  issuer?: string;
  type?: CardEventType;
  result?: CardEventResult;
  message?: string;
  occurred_at?: number;
  created_at?: number;
  fidelius_token?: string;
  card_state?: IssuedCardCardState;
  card_type?: CardType;
  customer_token?: string;
}

export enum CardEventResult {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
  ISSUER_EXCEPTION = "ISSUER_EXCEPTION",
  SUSPECTED_FRAUD = "SUSPECTED_FRAUD",
}

export enum CardEventType {
  ACTIVATED = "ACTIVATED",
  DISABLED = "DISABLED",
  ENABLED = "ENABLED",
  SUSPENDED = "SUSPENDED",
  REINSTATED = "REINSTATED",
  ISSUED = "ISSUED",
  TERMINATED = "TERMINATED",
  VERIFIED = "VERIFIED",
  SHIPPED = "SHIPPED",
  CANCELLED = "CANCELLED",
  REJECTED = "REJECTED",
  DESTROYED = "DESTROYED",
  SET_PIN = "SET_PIN",
  /** @deprecated */
  UNDELIVERABLE = "UNDELIVERABLE",
  REISSUED_LOST_OR_STOLEN = "REISSUED_LOST_OR_STOLEN",
  REISSUED_DAMAGED = "REISSUED_DAMAGED",
  UPDATED_IDV = "UPDATED_IDV",
  /** @deprecated */
  UPDATED_ADDRESS = "UPDATED_ADDRESS",
  UPDATED_EMAIL = "UPDATED_EMAIL",
  COURIER_FIRST_SCANNED = "COURIER_FIRST_SCANNED",
  COURIER_IN_HOME_FORECAST = "COURIER_IN_HOME_FORECAST",
  COURIER_UNDELIVERABLE = "COURIER_UNDELIVERABLE",
  UPDATED_USER = "UPDATED_USER",
  DEACTIVATED_USER = "DEACTIVATED_USER",
  ENABLED_ENHANCED_MONITORING = "ENABLED_ENHANCED_MONITORING",
  ACTIVATED_USER = "ACTIVATED_USER",
  SET_PRIMARY = "SET_PRIMARY",
  UNSET_PRIMARY = "UNSET_PRIMARY",
  UPDATED_BUSINESS = "UPDATED_BUSINESS",
  DISABLED_ENHANCED_MONITORING = "DISABLED_ENHANCED_MONITORING",
  ORDERED = "ORDERED",
  ACTIVATION_ATTEMPT = "ACTIVATION_ATTEMPT",
}
